import authService from '../../api/authService';
import userApi from '../../api/userApi';
import Avatar from '../../components/avatar/avatar';
import NavigationBar from '../../components/navigationBar';
import { Section } from '../../components/section';
import { PageTitle } from '@/components/pageTitle';
import { Box, Breadcrumbs, Container, Group } from '@mantine/core';
import { IconHome } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import _ from 'lodash';
import { Fragment, useMemo } from 'react';
import React from 'react';
import { useTranslator } from 'translations';

export const Route = createFileRoute('/_auth/profile')({
    component: RouteComponent,
});

function RouteComponent() {
    const t = useTranslator();
    const [appLogin] = authService.appLogin.useCache();
    const [allUsers] = userApi.useList({ accountEnabled: true });
    const lookup = useMemo(() => _.keyBy(allUsers, 'id'), [allUsers]);

    const manager = lookup[appLogin?.user.managerId ?? ''];

    const managedUsers = _(appLogin?.user.managedUsers)
        .filter(({ id }) => !!lookup[id]?.accountEnabled)
        .map((u) => ({ ...u, name: u.id, ...lookup[u.id] }))
        .orderBy('name')
        .value();

    const groups = {
        direct: managedUsers.filter((u) => u.depth === 0),
        indirect: managedUsers.filter((u) => u.depth !== 0),
    };

    return !appLogin ? null : (
        <PageTitle title={t('loggedInInfo.profile')}>
            <NavigationBar
                breadcrumbs={
                    <Breadcrumbs>
                        <Link to="/">
                            <IconHome />
                        </Link>
                        <Link to="/profile">{t('profilePage.profile')}</Link>
                    </Breadcrumbs>
                }
            />

            <Container
                size="md"
                css={{
                    padding: '0 var(--mantine-spacing-sm)',
                }}
            >
                <Section title={t('profilePage.personalInfo')}>
                    <Table>
                        <div>{t('profilePage.displayName')}</div>
                        <div>{appLogin.user.displayName}</div>

                        <div>{t('profilePage.email')}</div>
                        <div>{appLogin.user.mail}</div>

                        <div>{t('profilePage.employeeId')}</div>
                        <div>{appLogin.user.employeeId}</div>

                        <div>{t('profilePage.roles')}</div>
                        <div>
                            {appLogin.user.roles?.length ? appLogin.user.roles?.join(', ') : '-'}
                        </div>
                    </Table>
                </Section>

                {(manager || managedUsers.length) && (
                    <Section title={t('profilePage.colleagues')}>
                        <Table>
                            {manager && (
                                <>
                                    <div>{t('profilePage.manager')}</div>
                                    <Group>
                                        <Avatar userId={manager.id} />
                                        <div>{manager.name}</div>
                                    </Group>
                                </>
                            )}

                            {_.map(groups, (users, key: keyof typeof groups) =>
                                users.length === 0 ? null : (
                                    <Fragment key={key}>
                                        <div>{t(`profilePage.managedEmployees.${key}`)}</div>
                                        <Group>
                                            {users?.map(({ id, name }, index) => (
                                                <Group key={index}>
                                                    <Avatar userId={id} />
                                                    <div>{name}</div>
                                                </Group>
                                            ))}
                                        </Group>
                                    </Fragment>
                                ),
                            )}
                        </Table>
                    </Section>
                )}
            </Container>
        </PageTitle>
    );
}

function Table({ children }: { children: React.ReactNode }) {
    return (
        <Box
            css={{
                display: 'grid',

                '& > *:nth-of-type(2n+1)': {
                    fontSize: '0.75rem',
                },

                '& > *:nth-of-type(2n+3)': {
                    marginTop: 'var(--mantine-spacing-md)',
                },
            }}
        >
            {children}
        </Box>
    );
}
